import { Component, Input, OnChanges, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { PortalHotToastService, ApplicationDialogService, getUser } from '@portal-workspace/grow-ui-library';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { TagBoxComponent } from '../message-box/tag-box.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  ConfirmationDialogResult,
  DEFAULT_LIMIT,
  DEFAULT_OFFSET,
  DisableOverdraftUserFn,
  EditKycStatusDialogResult,
  EnableOverdraftUserFn,
  GetPismoAccountAndOpportunityDetailsFn,
  GetPismoCardsForCustomerFn,
  GetPismoCustomerForAccountFn,
  GetPismoCustomerLimitControlFn,
  GetTokenInfoFn,
  isAdminOrOperationUser,
  KycStatus,
  PismoAccountAndOpportunityResponse,
  PismoAccountMapping,
  PismoCardDetailsWithAccountId,
  PismoCardTokenInfo,
  PismoChangeCardStatusFn,
  PismoFlexControlBody,
  PismoFlexControlResponse,
  PismoGetCardReissueReasonsFn,
  PismoReissueCardFn,
  PismoUpdateCustomerFn,
  PortalLoginUser,
  SendForgotPasswordFn,
  SlideToggleValue,
  UpdateMonthlyLimitDialogResult,
  UpdatePismoCustomerLimitControlFn,
  UpdateUserKycStatusFn,
  UserWithPismoMapping
} from '@portal-workspace/grow-shared-library';
import { AsyncPipe, DatePipe, JsonPipe, NgClass, NgFor, NgIf, NgStyle, CurrencyPipe } from '@angular/common';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DisableControlDirective } from '../../directives/disable-control.directive';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { loadingFor } from '@ngneat/loadoff';
import { MatSortModule, Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import moment from 'moment';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CustomPaginatorComponent } from '../custom-paginator-component/custom-paginator/custom-paginator.component';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { FlexModule } from "@angular/flex-layout";
import { CustomContentLoaderComponent } from "../custom-content-loader-component/custom-content-loader.component";
import { MatIconModule } from '@angular/material/icon';
import { KycVerificationDetailsComponent } from '../kyc-verification-details-component/kyc-verification-details.component';

export interface DataSourceEntry {
  slideToggleFormControl: FormControl<SlideToggleValue>,
  item: PismoCardDetailsWithAccountId;
}

export class OverdraftUserDataSource extends DataSource<UserWithPismoMapping> {

  subject = new BehaviorSubject<UserWithPismoMapping[]>([]);

  connect(collectionViewer: CollectionViewer): Observable<UserWithPismoMapping[]> {
    return this.subject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.subject.complete();
  }

  update(users: UserWithPismoMapping[]) {
    this.subject.next(users);
  }
}

@UntilDestroy()
@Component({
  selector: 'overdraft-account-users',
  templateUrl: './overdraft-account-users.component.html',
  styleUrls: ['./overdraft-account-users.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  imports: [MatFormFieldModule,
    MatInputModule,
    FormsModule, InputMaskModule,
    ReactiveFormsModule, NgFor, NgIf,
    DisableControlDirective,
    CustomContentLoaderComponent,
    AsyncPipe,
    JsonPipe,
    NgClass,
    MatTableModule,
    MatDividerModule,
    MessageBoxComponent,
    TagBoxComponent,
    MatTooltipModule,
    CustomPaginatorComponent,
    MatButtonModule,
    MatSortModule,
    MatExpansionModule, FlexModule,
    DatePipe,
    NgStyle,
    MatIconModule,
    CurrencyPipe,
    KycVerificationDetailsComponent
  ]
})

export class OverdraftAccountUsersComponent implements OnInit, OnChanges, OnDestroy {

  @Input({ required: true }) disableOverdraftUserFn!: DisableOverdraftUserFn;
  @Input({ required: true }) enableOverdraftUserFn!: EnableOverdraftUserFn;
  @Input({ required: true }) existingPismoAccountMappings: PismoAccountMapping[] = [];
  @Input({ required: true }) getPismoCustomerForAccountFn!: GetPismoCustomerForAccountFn
  @Input({ required: true }) updateUserKycStatusFn!: UpdateUserKycStatusFn
  @Input({ required: true }) sendForgotPasswordFn!: SendForgotPasswordFn
  @Input({ required: true }) pismoUpdateCustomerFn !: PismoUpdateCustomerFn
  @Input({ required: true }) getPismoCardsForCustomerFn!: GetPismoCardsForCustomerFn
  @Input({ required: true }) pismoChangeCardStatusFn!: PismoChangeCardStatusFn;
  @Input({ required: true }) pismoGetCardReissueReasonsFn!: PismoGetCardReissueReasonsFn;
  @Input({ required: true }) pismoReissueCardFn!: PismoReissueCardFn;
  @Input({ required: true }) getTokenInfoFn!: GetTokenInfoFn;
  @Input({ required: true }) getPismoCustomerLimitControlFn!: GetPismoCustomerLimitControlFn;
  @Input({ required: true }) updatePismoCustomerLimitControlFn!: UpdatePismoCustomerLimitControlFn;
  @Input({ required: true }) getPismoAccountAndOpportunityDetailsFn!: GetPismoAccountAndOpportunityDetailsFn;

  userSubscriptions: Subscription[] = [];
  detailSubscriptions: Subscription[] = [];

  moment = moment
  isAdminOrOperationUser = isAdminOrOperationUser;

  pismoAccountAndOpportunityDetails: PismoAccountAndOpportunityResponse[] = []; 

  // enabled users table
  enabledUsersDataSource = new OverdraftUserDataSource();
  enabledUsersTotal = 0;
  enabledUsersLimit = DEFAULT_LIMIT;
  enabledUsersOffset = DEFAULT_OFFSET;
  enabledUsersSorts?: {
    prop: 'pismoCustomerNumber' | 'name' | 'access' | 'email' | 'dob' | 'mobileNumber',
    dir: 'ASC' | 'DESC'
  };
  enabledUsersDisplayColumns = ['pismoCustomerNumber', 'name', 'email', 'dob', 'mobileNumber', 'icon'];// 'access', 'verified', 'kycStatus', 'action'];
  enabledUsersExpandedElement: UserWithPismoMapping | null = null;
  enabledUsersCustomerLimit: PismoFlexControlResponse | null = null;

  // disabled users table
  disabledUsersDataSource: OverdraftUserDataSource = new OverdraftUserDataSource();
  disabledUsersTotal = 0;
  disabledUsersLimit = DEFAULT_LIMIT;
  disabledUsersOffset = DEFAULT_OFFSET;
  disabledUsersSorts?: {
    prop: 'pismoCustomerNumber' | 'name' | 'access' | 'email' | 'dob' | 'mobileNumber',
    dir: 'ASC' | 'DESC'
  };
  disabledUsersDisplayColumns = ['pismoCustomerNumber', 'name', 'email', 'dob', 'mobileNumber', 'icon'];// 'access', 'verified', 'kycStatus', 'action'];
  disabledUsersExpandedElement: UserWithPismoMapping | null = null;
  user: PortalLoginUser | null = getUser();
  canAccess = isAdminOrOperationUser(this.user)
  //cards
  cardsDataSource = new MatTableDataSource<PismoCardDetailsWithAccountId>([]);
  cardsDisplayColumns = this.canAccess? ['printed_name', 'last_4_digits', 'expiration_date', 'status', 'actions'] : ['printed_name', 'last_4_digits', 'expiration_date', 'status'];
  cardsTotalPerUser = 0;

  // Track the currently selected customer to prevent race conditions
  currentLoadingCustomerId: number | null = null;

  // common
  pismoAccountNumbers: number[] = [];
  collapsedVerificationResults = new Set<number>();

  loader = loadingFor(
    'loadingEnabledOverdraftUser',
    'loadingDisabledOverdraftUser',
    'disablingOverdraftUser',
    'loadingCardDetails',
    'loadingEnabledUsersCustomerLimit'
  );

  constructor(private toastService: PortalHotToastService,
    private dialogService: ApplicationDialogService,
  ) {
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['existingPismoAccountMappings'];
    if (change && change.currentValue && change.currentValue.length) {
      this.reload();
    }
  }

  // Method that only clears detail-related subscriptions
  private clearDetailRequests() {
    // Unsubscribe from existing detail subscriptions to prevent race conditions
    if (this.detailSubscriptions.length > 0) {
      this.detailSubscriptions.forEach(sub => sub.unsubscribe());
      this.detailSubscriptions = [];
    }
  }

  ngOnDestroy() {
    // Ensure all subscriptions are cleared on component destruction

    if (this.userSubscriptions.length > 0) {
      this.userSubscriptions.forEach(sub => sub.unsubscribe());
    }

    this.clearDetailRequests();
  }

  reload() {
    this.userSubscriptions.push(this.getPismoAccountAndOpportunityDetailsFn(this.existingPismoAccountMappings.map(m => m.pismoAccountNumber)).pipe(
      this.loader.loadingEnabledOverdraftUser.track()
    ).subscribe((response: PismoAccountAndOpportunityResponse[]) => {
      this.pismoAccountAndOpportunityDetails = response;
    }));
    this.reloadEnabledUsers();
    this.reloadDisabledUsers();
  }

  reloadEnabledUsers() {
    // Only clear detail subscriptions
    this.clearDetailRequests();

    if (this.existingPismoAccountMappings.length) {
      this.pismoAccountNumbers = (this.existingPismoAccountMappings ?? []).map(m => m.pismoAccountNumber);

      // Reset expanded element and current loading customer
      this.enabledUsersExpandedElement = null;
      this.currentLoadingCustomerId = null;

      // load enabled users (pismo customers)
      this.userSubscriptions.push(this.getPismoCustomerForAccountFn(this.pismoAccountNumbers, {
        page: {
          offset: this.enabledUsersOffset,
          limit: this.enabledUsersLimit,
        },
        sorts: this.enabledUsersSorts ? [this.enabledUsersSorts] : undefined,
      }, 'enabled').pipe(tap(r => {
        this.loader.loadingEnabledOverdraftUser.track();
        this.enabledUsersTotal = r.total;
        this.enabledUsersDataSource.update(r.payload);
      })).subscribe());
    }
  }

  reloadDisabledUsers() {
    // Only clear detail subscriptions 
    this.clearDetailRequests();

    if (this.existingPismoAccountMappings.length) {
      this.pismoAccountNumbers = (this.existingPismoAccountMappings ?? []).map(m => m.pismoAccountNumber);

      // Reset expanded element and current loading customer
      this.disabledUsersExpandedElement = null;
      this.currentLoadingCustomerId = null;

      // load disabled users (pismo customer)
      this.userSubscriptions.push(this.getPismoCustomerForAccountFn(this.pismoAccountNumbers, {
        page: {
          offset: this.disabledUsersOffset,
          limit: this.disabledUsersLimit,
        },
        sorts: this.disabledUsersSorts ? [this.disabledUsersSorts] : undefined,
      }, 'disabled').pipe(tap(r => {
        this.loader.loadingDisabledOverdraftUser.track();
        this.disabledUsersTotal = r.total;
        this.disabledUsersDataSource.update(r.payload);
      })).subscribe());
    }
  }

  onRowClickEnabledUser(row: UserWithPismoMapping) {
    console.log('row clicked', row)

    // Clear any pending detail requests first (not user loading requests)
    this.clearDetailRequests();

    // Toggle expanded state
    if (this.enabledUsersExpandedElement && this.enabledUsersExpandedElement.pismoCustomerNumber === row.pismoCustomerNumber) {
      this.enabledUsersExpandedElement = null;
      this.currentLoadingCustomerId = null;
      return;
    }

    // Set the expanded element and clear previous data
    this.enabledUsersExpandedElement = row;
    this.enabledUsersCustomerLimit = null;
    this.cardsDataSource.data = [];
    this.cardsTotalPerUser = 0;

    // Store the current customer ID to check in callbacks
    this.currentLoadingCustomerId = row.pismoCustomerNumber;
    const customerId = row.pismoCustomerNumber;

    this.detailSubscriptions.push(
      this.getPismoCustomerLimitControlFn(row.pismoAccountNumber, row.pismoCustomerNumber).pipe(
        this.loader.loadingEnabledUsersCustomerLimit.track()
      )
        .subscribe(
          (response: PismoFlexControlResponse[]) => {
            // Only update if this is still the current customer
            if (this.currentLoadingCustomerId !== customerId) {
              console.log('Ignoring limit data for previous customer', customerId);
              return;
            }

            console.log('=====pismo customer limit control: ', response)
            const pismoCustomerLimitControl = response;
            if (pismoCustomerLimitControl.length > 0) {
              this.enabledUsersCustomerLimit = pismoCustomerLimitControl.find(limit => limit.type === 'spending_limit') ?? null;
            }
            else {
              this.enabledUsersCustomerLimit = null;
            }
          }
        )
    );

    this.detailSubscriptions.push(
      this.getPismoCardsForCustomerFn(row.pismoAccountNumber, row.pismoCustomerNumber)
        .pipe(
          this.loader.loadingCardDetails.track()
        )
        .subscribe(
          (response: PismoCardDetailsWithAccountId[]) => {
            // Only update if this is still the current customer
            if (this.currentLoadingCustomerId !== customerId) {
              console.log('Ignoring card data for previous customer', customerId);
              return;
            }

            console.log('=====pismo customer card details: ', response)
            const pismoCustomerCardDetails = response;
            if (pismoCustomerCardDetails.length > 0) {
              this.cardsDataSource.data = response;
              this.cardsTotalPerUser = pismoCustomerCardDetails.length;
            }
            else {
              this.cardsDataSource.data = [];
              this.cardsTotalPerUser = 0;
            }
          }
        )
    );
  }

  onRowClickDisabledUser(row: UserWithPismoMapping) {
    // Clear any pending detail requests first (not user loading requests)
    this.clearDetailRequests();

    // Toggle expanded state or set new expanded element
    if (this.disabledUsersExpandedElement && this.disabledUsersExpandedElement.pismoCustomerNumber === row.pismoCustomerNumber) {
      this.disabledUsersExpandedElement = null;
      this.currentLoadingCustomerId = null;
    } else {
      this.disabledUsersExpandedElement = row;
      this.cardsDataSource.data = [];
      this.cardsTotalPerUser = 0;

      // Store current customer ID and load card data if needed
      this.currentLoadingCustomerId = row.pismoCustomerNumber;
      const customerId = row.pismoCustomerNumber;

      // Load card data for disabled user if needed
      this.detailSubscriptions.push(
        this.getPismoCardsForCustomerFn(row.pismoAccountNumber, row.pismoCustomerNumber)
          .pipe(
            this.loader.loadingCardDetails.track()
          )
          .subscribe(
            (response: PismoCardDetailsWithAccountId[]) => {
              // Only update if this is still the current customer
              if (this.currentLoadingCustomerId !== customerId) {
                console.log('Ignoring card data for previous customer', customerId);
                return;
              }

              const pismoCustomerCardDetails = response;
              if (pismoCustomerCardDetails.length > 0) {
                this.cardsDataSource.data = response;
                this.cardsTotalPerUser = pismoCustomerCardDetails.length;
              } else {
                this.cardsDataSource.data = [];
                this.cardsTotalPerUser = 0;
              }
            }
          )
      );
    }
  }

  onEnabledUsersSort($event: Sort) {
    if ($event.direction) {
      const dir = $event.direction === 'asc' ? 'ASC' : 'DESC';
      let prop = 'email';
      switch ($event.active) {
        case 'pismoCustomerNumber':
          prop = 'pismoCustomerNumber';
          break;
        case 'name':
          prop = 'name';
          break;
        case 'access':
          prop = 'access';
          break;
        case 'email':
          prop = 'email';
          break;
        case 'dob':
          prop = 'dob';
          break;
        case 'mobileNumber':
          prop = 'mobileNumber';
          break;
      }
      this.enabledUsersLimit = DEFAULT_LIMIT;
      this.enabledUsersOffset = DEFAULT_OFFSET;
      this.enabledUsersSorts = {
        prop: prop as any, dir
      };
      this.reloadEnabledUsers();
    } else {
      this.enabledUsersSorts = undefined;
      this.reloadEnabledUsers();
    }
  }

  onDisabledUsersSort($event: Sort) {
    if ($event.direction) {
      const dir = $event.direction === 'asc' ? 'ASC' : 'DESC';
      let prop = 'email';
      switch ($event.active) {
        case 'pismoCustomerNumber':
          prop = 'pismoCustomerNumber';
          break;
        case 'name':
          prop = 'name';
          break;
        case 'access':
          prop = 'access';
          break;
        case 'email':
          prop = 'email';
          break;
        case 'dob':
          prop = 'dob';
          break;
        case 'mobileNumber':
          prop = 'mobileNumber';
          break;
      }
      this.disabledUsersLimit = DEFAULT_LIMIT;
      this.disabledUsersOffset = DEFAULT_OFFSET;
      this.disabledUsersSorts = {
        prop: prop as any, dir
      };
      this.reloadDisabledUsers();
    } else {
      this.disabledUsersSorts = undefined;
      this.reloadDisabledUsers();
    }
  }

  onEnabledUsersPagination($event: PageEvent) {
    this.enabledUsersLimit = $event.pageSize;
    this.enabledUsersOffset = $event.pageIndex;
    this.reloadEnabledUsers();
  }

  onDisabledUsersPagination($event: PageEvent) {
    this.disabledUsersLimit = $event.pageSize;
    this.disabledUsersOffset = $event.pageIndex;
    this.reloadDisabledUsers();
  }

  onUpdateKycVerificationStatus(element: UserWithPismoMapping) {
    this.userSubscriptions.push(
      this.dialogService.openEditKycStatusDialog({
        kycStatus: element.kycStatus
      }).afterClosed().subscribe((result: EditKycStatusDialogResult | undefined) => {
        if (result && result.readyForSubmission) {
          console.log('============result: ', result)
          this.updateUserKycStatusFn(result.kycStatus as KycStatus, result.note, element.UserId)
            .pipe(
              this.toastService.spinnerObservable(),
            ).subscribe(() => { this.reload() })
        }
      })
    )
  }

  onUpdateMonthlyLimit(element: UserWithPismoMapping, cardDetails: PismoCardDetailsWithAccountId[], limit: PismoFlexControlResponse | null) {
    console.log('============cardDetails: ', cardDetails)
    console.log('============limit: ', limit)
    const accountId = element.pismoAccountNumber;
    const customerId = element.pismoCustomerNumber;

    const maxValue = this.pismoAccountAndOpportunityDetails.find(detail => detail.pismoAccountDetails.account_id === accountId)?.pismoAccountDetails.total_credit_limit ?? 0;
    console.log('============maxValue for monthly limit: ', maxValue)
    this.userSubscriptions.push(
      this.dialogService.openUpdateMonthlyLimitDialog({
        limit: limit?.max_limit ?? 0,
        maxValue: maxValue
      }).afterClosed().subscribe((result: UpdateMonthlyLimitDialogResult | undefined) => {
        if (result && result.readyForSubmission) {
          console.log('============result: ', result)
          const data: PismoFlexControlBody = {
            account_id: accountId,
            name: 'monthly limit control',
            type: 'spending_limit',
            card_ids: cardDetails.map(card => card.id),
            max_limit: result.maxLimit,
            active: true,
            limit_duration: 'P1M',
            deny_code: 'ERR_VAL_BALANCE',
          };
          console.log('============data: ', data)
          this.userSubscriptions.push(
            this.updatePismoCustomerLimitControlFn(customerId, data).pipe(
              this.toastService.spinnerObservable(),
            ).subscribe(() => { this.reload() })
          )
        }
      })
    )
  }

  defaultString(s: any): string {
    return s ? s : 'Not available';
  }

  resetPassword(email: string) {
    this.userSubscriptions.push(
      this.sendForgotPasswordFn({
        Email: email,
      }).pipe(
        this.toastService.spinnerObservable(),
        tap(r => {
          if (r.status) {
            this.dialogService.successDialog({
              message: 'Success',
              subMessage: `An email has been sent to ${email} to reset password.`
            })
          } else {
            this.dialogService.openAlertDialog({
              message: 'Error',
              subMessage: r.message,
            });
          }
        })
      ).subscribe()
    );
  }

  editPismoCustomer(userData: UserWithPismoMapping) {
    this.dialogService.openPismoEditCustomerDialog({
      item: userData,
      pismoUpdateCustomer: this.pismoUpdateCustomerFn
    })
      .afterClosed()
      .pipe(tap(r => {
        if (r)
          this.reload();
      })
      ).subscribe()

  }

  openCardsSettings(element: PismoCardDetailsWithAccountId) {
    this.detailSubscriptions.push(
      this.getTokenInfoFn(element.accountId, element.id)
        .pipe(
          this.toastService.spinnerObservable()
        )
        .subscribe(
          (response: PismoCardTokenInfo[]) => {
            console.log('=====pismo customer card details: ', response)
            this.dialogService.openPismoActivateOrDeactivateCardDialog({
              tokenInfos: response,
              cardDetails: element,
              pismoChangeCardStatus: this.pismoChangeCardStatusFn,
              pismoGetCardReissueReasonsFn: this.pismoGetCardReissueReasonsFn,
              pismoReissueCardFn: this.pismoReissueCardFn
            })
              .afterClosed()
              .pipe(
                tap(r => {
                  this.reload();
                })
              ).subscribe();
          }
        )
    )
  }

  disableUser(userId: number, pismoAccountNumber: number) {
    this.userSubscriptions.push(
      this.dialogService.openConfirmationDialog({
        message: "Please confirm",
        subMessage: "Do you want to disable this user?"
      }).afterClosed().subscribe((r: ConfirmationDialogResult | undefined) => {
        if (r && r.readyForSubmission) {
          this.userSubscriptions.push(this.disableOverdraftUserFn(userId, pismoAccountNumber).pipe(
            this.toastService.spinnerObservable(),
            this.loader.disablingOverdraftUser.track(),
            this.toastService.snackBarObservable(`User ${userId} with pismo account number ${pismoAccountNumber} disabled`),
            tap(r => {
              this.reload();
            })
          ).subscribe());
        }
      })
    )
  }

  enableUser(userId: number, pismoAccountNumber: number) {
      this.dialogService.openEnablePismoCustomerAccountDialog({
        userId:userId,
        pismoAccountNumber,
        enableOverdraftUserFn: this.enableOverdraftUserFn,
      })
      .afterClosed()
      .subscribe((r)=>{
        if(r && r.valid){
           this.reload();
        }
      });
  }

  // KYC Helper Methods - State Management
  
  isKycVerificationResultExpanded(element: UserWithPismoMapping): boolean {
    if (!element.pismoCustomerNumber) return false;
    return !this.collapsedVerificationResults.has(element.pismoCustomerNumber);
  }

  toggleKycVerificationResult(element: UserWithPismoMapping) {
    if (!element.pismoCustomerNumber) return;
    if (this.collapsedVerificationResults.has(element.pismoCustomerNumber)) {
      this.collapsedVerificationResults.delete(element.pismoCustomerNumber);
    } else {
      this.collapsedVerificationResults.add(element.pismoCustomerNumber);
    }
  }
}
